// 第三方组件二开，AI 生成注释，自行辨别参考
// @ts-nocheck
import { Coords, getElementCoords, dist, getElementDims, Dims, getCombinedData, fitsWithin, isWithinAt, isForeignTarget } from "./dom";
import { getViewportCenter, addAppropriateOffset, applyCenterOffset, centerViewportAroundElements, centerViewportAroundElement, getCurrentScrollOffset } from "./offset";
import { getViewportDims, getViewportScrollEnd, getScrolledViewportPosition, getViewportScrollStart, isElementInView, getViewportStart } from "./viewport";

export enum CardinalOrientation {
  EAST = 'east',
  SOUTH = 'south',
  WEST = 'west',
  NORTH = 'north',
  CENTER = 'center',
  EASTNORTH = 'east-north',
  EASTSOUTH = 'east-south',
  SOUTHEAST = 'south-east',
  SOUTHWEST = 'south-west',
  WESTSOUTH = 'west-south',
  WESTNORTH = 'west-north',
  NORTHWEST = 'north-west',
  NORTHEAST = 'north-east'
}

export interface OrientationCoords {
  orientation: CardinalOrientation;
  coords: Coords;
}

export interface GetTooltipPositionArgs {
  target: HTMLElement;
  tooltip: HTMLElement;
  padding: number;
  tooltipSeparation: number;
  root: Element;
  orientationPreferences?: CardinalOrientation[];
  getPositionFromCandidates?: (candidates: OrientationCoords[]) => OrientationCoords;
  disableAutoScroll?: boolean;
  allowForeignTarget?: boolean;
  selector?: string;
}

/**
 * 获取提示框位置候选集
 *
 * @param target 目标元素
 * @param tooltip 提示框元素
 * @param padding 提示框边距
 * @param tooltipDistance 提示框与目标元素距离
 * @param includeAllPositions 是否包含所有位置，默认为false
 * @returns 提示框位置候选集
 */
function getTooltipPositionCandidates(target: HTMLElement, tooltip: HTMLElement, padding: number, tooltipDistance: number, includeAllPositions?: boolean): OrientationCoords[] {
  if (!target || !tooltip) {
    return;
  }

  const tooltipDims: Dims = getElementDims(tooltip);
  const targetCoords: Coords = getElementCoords(target);
  const targetDims: Dims = getElementDims(target);
  const centerX: number = targetCoords.x - ((tooltipDims.width - targetDims.width) / 2);
  const centerY: number = targetCoords.y - ((tooltipDims.height - targetDims.height) / 2);
  const eastOffset: number = targetCoords.x + targetDims.width + padding + tooltipDistance;
  const southOffset: number = targetCoords.y + targetDims.height + padding + tooltipDistance;
  const westOffset: number = targetCoords.x - tooltipDims.width - padding - tooltipDistance;
  const northOffset: number = targetCoords.y - tooltipDims.height - padding - tooltipDistance;

  const east: Coords = { x: eastOffset, y: centerY }
  const south: Coords = { x: centerX, y: southOffset }
  const west: Coords = { x: westOffset, y: centerY };
  const north: Coords = { x: centerX, y: northOffset };
  const center: Coords = applyCenterOffset(targetCoords, targetDims, tooltipDims);

  const standardPositions = [
    { orientation: CardinalOrientation.EAST, coords: east },
    { orientation: CardinalOrientation.SOUTH, coords: south },
    { orientation: CardinalOrientation.WEST, coords: west },
    { orientation: CardinalOrientation.NORTH, coords: north },
  ];

  let additionalPositions: OrientationCoords[];
  if (includeAllPositions) {
    const eastAlign: number = targetCoords.x - (tooltipDims.width - targetDims.width) + padding;
    const southAlign: number = targetCoords.y - (tooltipDims.height - targetDims.height) + padding;
    const westAlign: number = targetCoords.x - padding;
    const northAlign: number = targetCoords.y - padding;

    const eastNorth: Coords = { x: eastOffset, y: northAlign }
    const eastSouth: Coords = { x: eastOffset, y: southAlign }
    const southEast: Coords = { x: eastAlign, y: southOffset }
    const southWest: Coords = { x: westAlign, y: southOffset }
    const westSouth: Coords = { x: westOffset, y: southAlign }
    const westNorth: Coords = { x: westOffset, y: northAlign }
    const northWest: Coords = { x: westAlign, y: northOffset }
    const northEast: Coords = { x: eastAlign, y: northOffset }

    additionalPositions = [
      { orientation: CardinalOrientation.EASTNORTH, coords: eastNorth },
      { orientation: CardinalOrientation.EASTSOUTH, coords: eastSouth },
      { orientation: CardinalOrientation.SOUTHEAST, coords: southEast },
      { orientation: CardinalOrientation.SOUTHWEST, coords: southWest },
      { orientation: CardinalOrientation.WESTSOUTH, coords: westSouth },
      { orientation: CardinalOrientation.WESTNORTH, coords: westNorth },
      { orientation: CardinalOrientation.NORTHWEST, coords: northWest },
      { orientation: CardinalOrientation.NORTHEAST, coords: northEast }
    ]
  }

  return [
    ...standardPositions,
    ...additionalPositions,
    { orientation: CardinalOrientation.CENTER, coords: center }
  ]
}

// simple reducer who selects for coordinates closest to the current center of the viewport
/**
 * 获取中心 reducer 函数
 *
 * @param root 根元素
 * @param tooltip 提示元素
 * @param target 目标元素
 * @param predictViewport 是否预测视口位置，默认为 false
 * @returns 返回 reducer 函数
 */
function getCenterReducer(root: Element, tooltip: HTMLElement, target: HTMLElement, predictViewport?: boolean):
  ((acc: OrientationCoords, cur: OrientationCoords, ind: number, arr: OrientationCoords[]) => OrientationCoords) {
  const currentCenter: Coords = getViewportCenter(root, tooltip);

  // 存储预测的视口位置中心，以便后续进行有意义的距离比较
  // store the center of the predicted viewport location with the tooltip at acc
  // to have a meaningful distance comparison
  let accCenter: Coords | undefined = currentCenter;

  const getCenter = (coords: Coords) => {
    // 如果需要预测视口位置，并且目标元素或提示元素不在视口中，则重新计算视口中心位置
    if (predictViewport && (!isElementInView(root, target) || !isElementInView(root, tooltip, coords, true))) {
      return getViewportCenter(root, tooltip, getScrolledViewportPosition(root, centerViewportAroundElements(root, tooltip, target, coords)));
    } else {
      // 否则返回当前中心位置
      return currentCenter;
    }
  }

  return (acc: OrientationCoords, cur: OrientationCoords, ind: number, arr: OrientationCoords[]): OrientationCoords => {
    // 忽略中心位置，因为中心位置总是最接近中心的
    if (cur.orientation === CardinalOrientation.CENTER) { //ignore centered coords since those will always be closest to the center
      // 除非已经遍历完所有位置，并且还没有选择过坐标，否则返回当前坐标
      if (ind === arr.length - 1 && acc === undefined) { //unless  we're at the end and we still haven't picked a coord
        return cur;
      } else {
        // 否则返回累积的坐标
        return acc;
      }
    } else if (acc === undefined) {
      // 如果累积的坐标未定义，则计算当前坐标的中心位置
      accCenter = getCenter(cur.coords);
      // 返回当前坐标
      return cur;
    } else {
      // 计算当前坐标的中心位置
      const center: Coords = getCenter(cur.coords);

      // 如果当前坐标中心与累积坐标的距离大于累积坐标中心与累积坐标的距离，则返回累积坐标
      if (dist(center, cur.coords) > dist(accCenter, acc.coords)) {
        return acc;
      } else {
        // 否则更新累积坐标中心，并返回当前坐标
        accCenter = center;
        return cur;
      }
    }
  }
}


// filter out any positions which would have the tooltip be out of the bounds of the root container 
// (i.e. in a position that the viewport can't "reach"/scroll to)
/**
 * 获取在边界内的过滤器函数
 *
 * @param tooltipDims 提示框的宽高信息
 * @param viewportScrollStart 视口滚动开始位置
 * @param viewportScrollEnd 视口滚动结束位置
 * @returns 返回一个函数，用于判断坐标是否在视口内
 */
function getInBoundsFilter(tooltipDims: Dims, viewportScrollStart: Coords, viewportScrollEnd: Coords): (oc: OrientationCoords) => boolean {
  // 返回一个函数，用于判断坐标是否在边界内
  return (oc: OrientationCoords): boolean => {
    // 获取传入的坐标值
    const coords: Coords = oc.coords;
    // 判断坐标是否在边界内
    return !(coords.x < viewportScrollStart.x || coords.y < viewportScrollStart.y ||
      (coords.x + tooltipDims.width) > viewportScrollEnd.x || (coords.y + tooltipDims.height) > viewportScrollEnd.y)
  }
}

// filters out any positions which would cause the target/tooltip to not fit within the viewport
/**
 * 获取绝对兼容的布局过滤器
 *
 * @param curriedGetCombinedData 获取组合数据的函数，参数为坐标信息，返回值为包含dims和coords的对象
 * @param viewportDims 视口尺寸
 * @returns 返回一个函数，输入参数为方向坐标，返回值为boolean类型，表示是否满足布局要求
 */
function getAbsoluteCompatibleArrangementFilter(curriedGetCombinedData: (coords: Coords) => { dims: Dims, coords: Coords }, viewportDims: Dims): (oc: OrientationCoords) => boolean {
  return (oc: OrientationCoords): boolean => {
    const coords: Coords = oc.coords;
    // we only care about the resultant dims but the input coords are critical here
    const { dims: combinedDims } = curriedGetCombinedData(coords);

    return fitsWithin(combinedDims, viewportDims);
  }
}

/**
 * 获取当前在视图中的过滤器
 *
 * @param curriedGetCombinedData 获取组合数据的柯里化函数，参数为坐标对象，返回值为一个包含dims和coords的对象
 * @param viewportDims 视图维度对象
 * @param viewportCurrentStart 当前视图起始坐标
 * @returns 返回一个函数，参数为方向坐标对象，返回值为布尔值，表示当前坐标是否在视图中
 */
function getCurrentInViewFilter(curriedGetCombinedData: (coords: Coords) => { dims: Dims, coords: Coords }, viewportDims: Dims, viewportCurrentStart: Coords): (oc: OrientationCoords) => boolean {
  return (oc: OrientationCoords): boolean => {
    const coords: Coords = oc.coords;

    const { dims: combinedDims, coords: combinedCoords } = curriedGetCombinedData(coords);

    return isWithinAt(combinedDims, viewportDims, combinedCoords, viewportCurrentStart);
  }
}

// complex candidate reducer function that tries to place the tooltip as close to the center of the 
// screen as possible, even after the screen has scrolled to a particular location.
/**
 * 选择最佳提示框位置
 *
 * @param preferredCandidates 首选坐标数组
 * @param root 根元素
 * @param tooltip 提示框元素
 * @param target 目标元素
 * @param scrollDisabled 是否禁用滚动
 * @returns 最佳坐标
 */
function chooseBestTooltipPosition(preferredCandidates: OrientationCoords[], root: Element, tooltip: HTMLElement, target: HTMLElement, scrollDisabled: boolean): OrientationCoords {
  if (preferredCandidates.length === 1) {
    //if there's only a single pref candidate, use that
    return preferredCandidates[0];
  } else if (scrollDisabled) {
    // if scrolling is disabled, there's not much we can do except use the naive center reducer
    return preferredCandidates.reduce(getCenterReducer(root, tooltip, target, false), undefined);
  } else {
    // scrolling is allowed, which means we have to figure out:
    // 1. what candidates are valid positions (not out of the scrolling root's bounds)
    // 2. which positions are absolutely compatible (allow both target & tooltip to fit within the viewport at the same time)
    // 3. which positions are currently compatible (allow both target & tooltip to fit with the CURRENT viewport)
    // 4. which of those positions is *best* - use same closest-to-center heuristic.
    // priority is 3 > 2 > 1 for the pool of positions from which 4 is chosen

    const viewportDims: Dims = getViewportDims(root);
    const viewportScrollStart: Coords = getViewportScrollStart(root);
    const viewportCurrentStart: Coords = getViewportStart(root);
    const viewportScrollEnd: Coords = getViewportScrollEnd(root);
    const tooltipDims: Dims = getElementDims(tooltip);
    const targetDims: Dims = getElementDims(target);
    const targetCoords: Coords = getElementCoords(target);
    const curriedGetCombinedData = (coords: Coords) => getCombinedData(coords, tooltipDims, targetCoords, targetDims);

    const validPositions: OrientationCoords[] = preferredCandidates.filter(getInBoundsFilter(tooltipDims, viewportScrollStart, viewportScrollEnd));
    const absoluteCompatiblePositions: OrientationCoords[] = validPositions.filter(getAbsoluteCompatibleArrangementFilter(curriedGetCombinedData, viewportDims));
    const currentCompatiblePositions: OrientationCoords[] = absoluteCompatiblePositions.filter(getCurrentInViewFilter(curriedGetCombinedData, viewportDims, viewportCurrentStart));

    // // if possible, use only those positions which don't force a scroll. Default back to those which can fit in the viewport, even if that means scrolling
    const compatiblePositions: OrientationCoords[] = currentCompatiblePositions.length > 0 ? currentCompatiblePositions : absoluteCompatiblePositions;

    // if there are NO compatible positions, the viewport is too small to accomodate both the target/tooltip, in any arrangement.
    // we default to our valid positions, even if that means placing the elements slightly off screen.
    const filteredList = compatiblePositions.length > 0 ? compatiblePositions : validPositions;

    return filteredList.reduce(getCenterReducer(root, tooltip, target, true), undefined);
  }
}

/**
 * 根据给定的方向偏好，从候选方向中返回首选的方向列表。
 *
 * @param candidates 候选方向列表
 * @param orientationPreferences 方向偏好列表，可选
 * @returns 返回首选的方向列表
 */
function getPreferredCandidates(candidates: OrientationCoords[], orientationPreferences?: CardinalOrientation[]): OrientationCoords[] {
  // 如果没有指定方向偏好，或者方向偏好数组为空，则直接返回候选人列表
  if (!orientationPreferences || orientationPreferences.length === 0) {
    return candidates;
  } else if (orientationPreferences.length === 1) {
    // 如果指定了一个方向偏好
    const specifiedCandidate = candidates.find((oc: OrientationCoords) => oc.orientation === orientationPreferences[0])
    // 如果找到了与指定方向偏好匹配的候选人
    if (specifiedCandidate) {
      // 则只返回该候选人
      return [specifiedCandidate];
    } else {
      // 如果找不到与指定方向偏好匹配的候选人，则默认返回原始候选人列表
      return candidates; // if the specified orientation isn't available for whatever reason, default to standard behavior
    }
  } else {
    // 如果指定了多个方向偏好
    const preferenceFilter = (cc: OrientationCoords) => orientationPreferences.indexOf(cc.orientation) !== -1;
    // 使用过滤函数筛选出符合方向偏好的候选人
    return candidates.filter(preferenceFilter);
  }
}

/**
 * 限制坐标在当前视口范围内
 *
 * @param root 根元素
 * @param coords 坐标
 * @param dims 尺寸
 * @param padding 内边距
 * @returns 返回限制后的坐标
 */
function restrictToCurrentViewport(root: Element, coords: Coords, dims: Dims, padding: number): Coords {
  // 如果根元素不存在，则直接返回原始坐标
  if (!root) {
    return coords;
  }

  // 获取视口的起始坐标
  const viewportStart: Coords = getCurrentScrollOffset(root);
  // 获取视口的尺寸
  const viewportDims: Dims = getViewportDims(root);
  // 计算视口的结束坐标
  const viewportEnd: Coords = {
    x: viewportStart.x + viewportDims.width,
    y: viewportStart.y + viewportDims.height
  }

  // 计算视口起始坐标加上内边距后的坐标
  const sx = viewportStart.x + padding;
  const sy = viewportStart.y + padding;
  // 计算视口结束坐标减去元素尺寸和内边距后的坐标
  const ex = viewportEnd.x - dims.width - padding;
  const ey = viewportEnd.y - dims.height - padding;

  let x: number = coords.x;
  let y: number = coords.y;

  // 如果元素的左边界小于视口起始坐标加上内边距后的坐标，则将元素的左边界设置为视口起始坐标加上内边距后的坐标
  if (coords.x < sx) {
    x = sx;
  // 如果元素的右边界大于视口结束坐标减去元素尺寸和内边距后的坐标，则将元素的左边界设置为视口结束坐标减去元素尺寸和内边距后的坐标
  } else if ((coords.x + dims.width) > ex) {
    x = ex;
  }

  // 如果元素的上边界小于视口起始坐标加上内边距后的坐标，则将元素的上边界设置为视口起始坐标加上内边距后的坐标
  if (coords.y < sy) {
    y = sy;
  // 如果元素的下边界大于视口结束坐标减去元素尺寸和内边距后的坐标，则将元素的上边界设置为视口结束坐标减去元素尺寸和内边距后的坐标
  } else if ((coords.y + dims.height) > ey) {
    y = ey;
  }

  // 返回调整后的坐标
  return { x, y }
}

/**
 * 获取提示框位置
 *
 * @param args 参数对象
 * @returns 提示框位置
 */
export function getTooltipPosition(args: GetTooltipPositionArgs): OrientationCoords {
  const { target, tooltip, padding, tooltipSeparation, orientationPreferences, getPositionFromCandidates, root: tourRoot, disableAutoScroll: scrollDisabled, allowForeignTarget, selector } = args;

  // 获取目标元素的中心点位置
  const center: Coords = target ? getViewportCenter(tourRoot, tooltip, getScrolledViewportPosition(tourRoot, centerViewportAroundElement(tourRoot, target))) : getViewportCenter(tourRoot, tooltip)

  // 添加适当的偏移量得到默认位置
  const defaultPosition: Coords = addAppropriateOffset(tourRoot, center);

  // 如果tooltip或tourRoot不存在，则直接返回
  if (!tooltip || !tourRoot) {
    return;
  }

  // 如果没有目标元素，则返回默认位置
  if (!target) {
    return {orientation: null, coords: defaultPosition};
  }

  // 判断目标元素是否为外部元素
  const foreignTarget: boolean = allowForeignTarget && isForeignTarget(tourRoot, selector);

  // 判断是否禁止自动滚动
  const noScroll: boolean = scrollDisabled || foreignTarget;

  // 获取tooltip位置候选列表
  const candidates: OrientationCoords[] = getTooltipPositionCandidates(target, tooltip, padding, tooltipSeparation, true);

  // 获取位置选择函数
  const choosePosition = getPositionFromCandidates || ((cans: OrientationCoords[]) => chooseBestTooltipPosition(cans, tourRoot, tooltip, target, noScroll));

  // 根据偏好选择最佳位置
  const rawPosition: OrientationCoords = choosePosition(getPreferredCandidates(candidates, orientationPreferences)); //position relative to current viewport

  // 如果没有找到合适的位置，则返回默认位置
  if (!rawPosition) {
    return {orientation: CardinalOrientation.CENTER, coords: defaultPosition};
  }

  // 根据需要调整位置
  const adjustedPosition: OrientationCoords = {orientation: rawPosition.orientation, coords: addAppropriateOffset(tourRoot, rawPosition.coords)};

  // 如果目标元素是外部元素，则限制位置在当前视口内
  if (foreignTarget) {
    return {orientation: adjustedPosition.orientation, coords: restrictToCurrentViewport(tourRoot, adjustedPosition.coords, getElementDims(tooltip), padding + tooltipSeparation)}
  }

  // 返回调整后的位置
  return adjustedPosition;
}

export function getTargetPosition(root: Element, target: HTMLElement): Coords {
  return addAppropriateOffset(root, getElementCoords(target));
}
